import { objectToUrlParams } from '@/utils/utils';

import { rtkQueryApi } from './rtk-query-api';

interface GetCardHistoryParams {
  projectId: string;
  productId: string;
  dataType: 'product' | 'queries' | 'words';
}

interface GetCardHistoryResponse {
  productChangeHistory: {
    name: string;
    description: string;
    id: string;
    date: string;
  }[];
  newCharacteristics: { name: string; value: string }[];
  oldCharacteristics: { name: string; value: string }[];
}

interface GetProductCardDatParams {
  productId: string;
}

interface ProductDataResponse {
  name: string;
  description: string;
  characteristics: { name: string; value: string }[];
}

interface GetCharcsCardResponse {
  charcs: {
    charcID: number;
    name: string;
    required: boolean;
    unitName: string;
    maxCount: number;
    popular: boolean;
    charcType: number;
  }[];
  newCharacteristics: { name: string; value: string }[];
  oldCharacteristics: { name: string; value: string }[];
}

interface GetProductCardDatParams {
  productId: string;
}

interface ProductDataResponse {
  name: string;
  description: string;
  characteristics: { name: string; value: string }[];
}

export interface SaveCardDataParams {
  art: string;
  data: SaveCardDataPayload;
}

export interface Characteristic {
  charcID?: number;
  name: string;
  value: string | number | (string | number)[] | null;
  charcType?: number;
}

export interface SaveCardDataPayload {
  update: {
    data: {
      title: string;
      description: string;
    };
    characteristics: Characteristic[];
  };
  upload: {
    characteristics: Characteristic[];
  };
}

export type GetRecommendDataResponse = Record<string, object>;

export const cardAnalyzeApi = rtkQueryApi.injectEndpoints({
  endpoints: (builder) => ({
    getCardHistory: builder.mutation<GetCardHistoryResponse, GetCardHistoryParams>({
      query: ({ projectId, productId, dataType }) =>
        `project/card-analyse?${objectToUrlParams({
          pid: projectId,
          art: productId,
          key: ['product', 'queries', 'words'].indexOf(dataType) + 1,
        })}`,
      transformResponse: (response) => ({
        productChangeHistory: response.history.map((item) => ({
          name: item.title,
          description: item.desc,
          id: String(item.id),
          date: item.dt,
        })),
        newCharacteristics: response.options_new.map((item) => ({
          name: item.name,
          value: item.value,
        })),
        oldCharacteristics: response.options_old.map((item) => ({
          name: item.name,
          value: item.value,
        })),
      }),
    }),
    getCardData: builder.query<ProductDataResponse, GetProductCardDatParams>({
      query: ({ productId }) => ({
        url: `/card/sync/${productId}`,
        method: 'GET',
      }),
      transformResponse: (response: any): ProductDataResponse => ({
        name: response.title,
        description: response.description,
        characteristics: response.characteristics,
      }),
    }),
    getCardDataWithApiKey: builder.query<ProductDataResponse, { productId: number }>({
      query: ({ productId }) => ({
        url: `/card/get-card?art=${productId}`,
        method: 'GET',
      }),
      transformResponse: (response: any): ProductDataResponse => ({
        name: response.card.title,
        description: response.card.description,
        characteristics: response.card.characteristics.map(
          (char: any): Characteristic => ({
            ...char,
            value: Array.isArray(char.value)
              ? char.value.join(';')
              : typeof char.value === 'number'
                ? String(char.value)
                : char.value || '',
          }),
        ),
      }),
    }),
    getCharcsCard: builder.query<GetCharcsCardResponse, { art: number }>({
      query: ({ art }) => `/card/get-charcs-card?art=${art}`,
      transformResponse: (response: any): GetCharcsCardResponse => {
        const optionsMap: Record<string, keyof typeof response.lists> = {
          Цвет: 'colors',
          'Страна производства': 'countries',
          Пол: 'kinds',
          'Ставка НДС': 'vat',
        };
        const charcsWithOptions = response.charcs.map((charc: any) => {
          const listKey = optionsMap[charc.name];
          if (listKey) {
            return {
              ...charc,
              options: response.lists[listKey].map((item: any) => item.name || item),
            };
          }
          return charc;
        });
        return {
          charcs: charcsWithOptions,
        };
      },
    }),
    saveCardData: builder.mutation<void, SaveCardDataParams>({
      query: ({ art, data }) => ({
        url: `/card/edit-card?art=${art}`,
        method: 'POST',
        body: data,
      }),
    }),
    getRecommendsData: builder.query<
      GetRecommendDataResponse,
      { subjectId: number; productId: number }
    >({
      query: ({ subjectId, productId }) => ({
        url: `/seo/get-recommends/${subjectId}/${productId}`,
        method: 'GET',
      }),
    }),
    getCardCharacteristics: builder.query<GetRecommendDataResponse, { arts: number[] }>({
      query: ({ arts }) => ({
        url: `/card/characteristics?arts=${arts.join(',')}`,
        method: 'GET',
      }),
      transformResponse: (response: any) => response.data,
    }),
  }),
});

export const {
  useGetCardHistoryMutation,
  useLazyGetCardDataQuery,
  useGetCardDataQuery,
  useGetCardDataWithApiKeyQuery,
  useLazyGetCardDataWithApiKeyQuery,
  useGetCharcsCardQuery,
  useSaveCardDataMutation,
  useLazyGetRecommendsDataQuery,
  useGetCardCharacteristicsQuery,
} = cardAnalyzeApi;
